<script setup lang="ts">
import type { HeaderTabManagementEmits, HeaderTabManagementProps } from '.';
import type { Tab } from '~/components/TabsNavigator';

import { ArrowLeftIcon } from '@radix-icons/vue';
import { cn } from '~/utils';

import { TabsNavigator } from '~/components/TabsNavigator';
import TextLink from '~/components/ui/text-link/TextLink.vue';

import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';

const props = withDefaults(defineProps<HeaderTabManagementProps>(), {
	currentCarouselItemIndex: 0,
	totalItems: 0,
	isShareContentVisible: false
});

const emit = defineEmits<HeaderTabManagementEmits>();

const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);

defineExpose({
	shareContentRef,
	shareContentButton
});
</script>

<template>
	<Container class="!py-0 px-0">
		<DialogTitle
			:class="
				cn('flex size-full items-center md:pb-0', {
					'pb-5': props.isCarouselOpen
				})
			"
		>
			<div
				class="relative flex w-full items-start justify-start md:flex-nowrap md:items-center md:justify-center"
				:class="{ 'w-1/2 flex-wrap': !props.isCarouselOpen }"
			>
				<div
					:class="{
						'md:w-full': !props.isCarouselOpen
					}"
					class="order-1 flex w-1/2 items-center justify-between md:order-1 md:w-auto md:px-2"
				>
					<TextLink
						:element="'a'"
						class="flex items-center gap-2"
						@click="emit('go-back')"
					>
						<ArrowLeftIcon class="size-5" />
						<span class="text-base font-normal">Voltar</span>
					</TextLink>
				</div>

				<div
					v-if="!props.isCarouselOpen"
					class="order-3 flex w-full md:order-2 md:justify-center"
				>
					<TabsNavigator
						:model-value="props.currentTab"
						:tabs="props.tabs"
						class="flex gap-5 md:gap-6"
						container-class="!justify-start md:!justify-center"
						@update:model-value="
							(value: Tab | null | undefined) =>
								value && emit('update:currentTab', value)
						"
					/>
				</div>
				<div
					v-else
					:class="{
						'md:w-full': !props.isCarouselOpen,
						'w-full': props.isCarouselOpen
					}"
					class="order-2 flex grow justify-center px-10 font-matter-sq text-[18px] font-normal leading-[27px]"
				>
					{{ props.currentCarouselItemIndex + 1 }} de
					{{ props.tabs.find(tab => tab.id === props.currentTab)?.label }}
				</div>

				<div
					v-if="props.isCarouselOpen"
					class="order-3 block w-1/2 md:hidden"
				/>

				<div
					v-if="!props.isCarouselOpen"
					class="order-2 flex w-1/2 items-start justify-end md:order-3 md:w-full md:items-center"
				>
					<div class="flex flex-col items-start">
						<button
							ref="shareContentButton"
							class="z-10 hidden size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:flex"
							@click="emit('toggle-share')"
						>
							<IconMaterialSymbolsShare
								class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
							/>
						</button>

						<button
							class="z-10 flex size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:hidden"
							@click="emit('share-mobile')"
						>
							<IconMaterialSymbolsShare
								class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
							/>
						</button>
					</div>

					<div
						v-show="props.isShareContentVisible"
						ref="shareContentRef"
						class="absolute -top-7 right-0 w-[125px] translate-y-[60%] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
					>
						<slot name="share-content" />
					</div>
				</div>
			</div>
		</DialogTitle>
	</Container>
</template>
